/**
 * PostHog client analytics — two-tier consent, PII-free (DTAC C3.6 / PECR).
 *
 * Rules this module enforces:
 *  - Inert without a key: no VITE_POSTHOG_KEY → every call is a no-op
 *    (tests, local dev and CI never phone home).
 *  - EU Cloud only (data residency — same UK/EU story as the backend).
 *  - Two-tier consent (PECR + UK GDPR): the default tier is COOKIELESS —
 *    memory-only persistence stores nothing on the device, so no cookie
 *    consent is needed (legitimate-interests basis, disclosed in the banner
 *    and privacy notice). Accepting cookies upgrades persistence so visits
 *    can be linked (uniques / returning visitors); declining stays cookieless.
 *    The choice is remembered in localStorage (PECR-exempt).
 *  - No resident content in EITHER tier: autocapture and session recording
 *    are disabled — only the explicit, metadata-only events below are sent.
 *    Property keys that could carry PII are rejected at the call site.
 */
import posthog from "posthog-js";

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://eu.i.posthog.com";

/** localStorage key remembering the resident's analytics choice. */
export const CONSENT_STORAGE_KEY = "llr_analytics_consent";
export type ConsentChoice = "granted" | "denied";

/** Mirrors the backend audit() PII guard — these keys must never be sent. */
const PII_KEYS = new Set([
  "first_name", "last_name", "name", "full_postcode", "postcode", "email",
  "phone", "dob", "date_of_birth", "verbatim", "message", "text", "answer",
  "free_text", "patient_identifier", "nhs_number",
]);

const enabled = (): boolean => Boolean(KEY);

let initialised = false;

/** Initialise PostHog (idempotent). Called once at app start. */
export function initAnalytics(): void {
  if (!enabled() || initialised) return;
  initialised = true;
  posthog.init(KEY!, {
    api_host: HOST,
    // Two-tier consent model (PECR + UK GDPR legitimate interests):
    //  - Default = COOKIELESS ("memory" persistence): nothing is stored on the
    //    device, so no cookie consent is required. Anonymous per-visit
    //    analytics runs for everyone — disclosed in the banner + privacy
    //    notice (LI basis; DPO sign-off required).
    //  - Accepting cookies upgrades persistence so visits can be linked
    //    (accurate uniques / returning visitors). Declining stays cookieless.
    persistence: "memory",
    // Explicit events only — never harvest DOM content or replay sessions in
    // a mental-health triage app.
    autocapture: false,
    disable_session_recording: true,
    // Pageview + pageleave: the two PASSIVE timing signals we DO want. A
    // $pageview on load and a $pageleave on tab-close give an accurate
    // "closed the tab here" drop-off point and a real session duration
    // ($session_duration / last-minus-first), instead of only inferring
    // drop-off from the last question_viewed. Safe on this app: the resident
    // URL is a single SPA route with NO PII in the path or query (verified —
    // the only query param is an optional `?api=` dev override), so the
    // $current_url these events carry leaks nothing. This is NOT autocapture:
    // no clicks, DOM, inputs, or content are harvested — just the two
    // navigation timestamps.
    capture_pageview: true,
    capture_pageleave: true,
    // Everything below is off by DESIGN, not by dashboard setting. PostHog
    // ships remote-config-driven extras that default on and can be flipped
    // from the PostHog UI — on a mental-health triage page, nothing may reach
    // a resident that isn't in this repo and reviewed. Verified live: these
    // scripts were being fetched (surveys.js / dead-clicks-autocapture.js /
    // web-vitals.js) before these flags were added.
    disable_surveys: true,          // never pop a survey at a distressed resident
    capture_dead_clicks: false,     // click-behaviour harvesting
    capture_performance: false,     // web vitals / network timing
    capture_heatmaps: false,        // pointer/scroll heatmaps
    disable_external_dependency_loading: true, // no remote extension scripts at all
  });
  // Returning visitor who previously accepted cookies → restore cookie mode.
  if (getConsent() === "granted") {
    posthog.set_config({ persistence: "localStorage+cookie" });
  }
}

/** The stored consent choice, or null if the resident hasn't chosen yet. */
export function getConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Record the resident's cookie choice. Granting upgrades persistence to
 * cookies (cross-visit identity); declining stays in cookieless memory mode —
 * anonymous per-visit analytics continues either way, as the banner states.
 */
export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    /* private browsing — the banner will just re-ask next visit */
  }
  if (!enabled()) return;
  if (choice === "granted") {
    posthog.set_config({ persistence: "localStorage+cookie" });
  }
  track(choice === "granted" ? "consent_granted" : "consent_denied", {});
}

/**
 * Send one analytics event. No-op when PostHog is disabled (no key). Events
 * flow in both tiers — cookieless (anonymous, per-visit) and cookie mode —
 * the tiers differ only in persistence, never in event content.
 * Throws in dev if a PII-named property key sneaks in.
 */
export function track(event: string, properties: Record<string, unknown> = {}): void {
  for (const k of Object.keys(properties)) {
    if (PII_KEYS.has(k)) {
      if (import.meta.env.DEV) throw new Error(`analytics: PII-named property "${k}" rejected`);
      return; // prod: drop the event rather than risk it
    }
  }
  if (!enabled()) return;
  posthog.capture(event, properties);
}

/**
 * Attach the backend triage session id to every subsequent event (super
 * property), so client behaviour can be joined to backend outcomes.
 * DPIA note: this makes the PostHog profile PSEUDONYMOUS — linkable to the
 * triage record while the PII store retains that session (90 days) — rather
 * than fully anonymous. Recorded in the PostHog DPIA entry.
 */
export function registerSession(sessionId: string): void {
  if (!enabled()) return;
  posthog.register({ triage_session_id: sessionId });
}

// ---------------------------------------------------------------------------
// Flow events — per-question timing + drop-off, all metadata-only.
// ---------------------------------------------------------------------------

let viewedNodeId: string | null = null;
let viewedAt = 0;
let flowCompleted = false;
let leftSent = false;

/** A new question/node is on screen. */
export function trackQuestionViewed(nodeId: string | null | undefined, nodeType: string): void {
  viewedNodeId = nodeId ?? null;
  viewedAt = Date.now();
  track("question_viewed", { node_id: viewedNodeId, node_type: nodeType });
}

export type InputKind = "typed" | "chip" | "option";

/** The resident answered the question that was on screen. */
export function trackQuestionAnswered(inputKind: InputKind = "option"): void {
  if (!viewedNodeId) return;
  track("question_answered", {
    node_id: viewedNodeId,
    input_kind: inputKind,
    seconds_on_question: Math.round((Date.now() - viewedAt) / 1000),
  });
}

/** The answer failed client-side validation and the question was re-asked. */
export function trackQuestionRetried(): void {
  track("question_retried", { node_id: viewedNodeId });
}

export function trackFlowStarted(): void {
  track("flow_started", {});
}

/** Terminal turn reached — completion, signpost or crisis exit. */
export function trackFlowEnded(opts: { crisis: boolean; service?: string | null }): void {
  flowCompleted = true;
  track(opts.crisis ? "crisis_exit" : "flow_completed", {
    ...(opts.crisis ? {} : { service_recommended: opts.service ?? "none" }),
  });
}

/**
 * Exit-mode-agnostic drop-off: fire ONE `flow_left` event when the resident
 * leaves the page — by closing the tab, backgrounding it (mobile), or
 * navigating away — from ANY point (mid-question, on the completion screen,
 * or after the reference number). This is the explicit-leave half of the
 * drop-off picture; the silent walk-away (never closes the tab) is still
 * covered by the last `question_viewed` with no matching `question_answered`.
 *
 * Why not rely on PostHog's `$pageleave`: on this single-route SPA it does not
 * fire reliably, and it carries no node context. `flow_left` carries the node
 * the resident was on and whether they had already completed — so "closed on
 * the completion screen after getting the reference number" is distinguishable
 * from "abandoned at Q_POSTCODE".
 *
 * Fires on `pagehide` — the reliable "this page is going away" signal. It
 * covers tab close, navigation away, and (via bfcache) mobile app-switch /
 * lock, and unlike `beforeunload` it doesn't block bfcache or misfire on
 * desktop. Deliberately NOT `visibilitychange→hidden`: that fires on a brief
 * tab-switch the resident returns from, which would falsely mark a completed
 * session as a drop-off. A momentary hide is not leaving; `pagehide` is.
 * Sends at most once. Metadata-only; same consent tier as every other event.
 */
export function installLeaveTracking(): void {
  if (!enabled() || typeof window === "undefined") return;
  window.addEventListener("pagehide", () => {
    if (leftSent) return;
    leftSent = true;
    track("flow_left", {
      node_id: viewedNodeId,
      flow_completed: flowCompleted,
      seconds_on_question: viewedAt ? Math.round((Date.now() - viewedAt) / 1000) : null,
    });
  });
}
